import { useQueryClient } from '@tanstack/react-query';
import React from 'react';

import { StateHandlingModal, StateHandlingModalProps } from '@eika-kortbetjening/state-handling-modal';
import { ErrorCard } from '@eika/card';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import CouldNotFetchVirtualCard from '@/components/card/card-info-with-functionality/virtual-card/CouldNotFetchVirtualCard';
import VirtualCard from '@/components/card/card-info-with-functionality/virtual-card/VirtualCard';
import { useKbVirtualCreditCard } from '@/domain/card';

import { CreditCard, VirtualCard as VirtualCardI } from '@generated-types';
import { useIsHighFraudRisk } from '@hooks/use-is-high-risk';
import useText from '@hooks/use-text';
import DEFAULT_APP_NAMESPACE from '@utils/modal';

interface VirtualCardModalProps extends StateHandlingModalProps {
    card: CreditCard;
}

const VirtualCardNotAvailable = () => {
    const { digitalCardErrorTitle, digitalCardErrorDescription } = useText();

    return (
        <ErrorCard
            isModal
            title={digitalCardErrorTitle}
            description={digitalCardErrorDescription}
            icon={<RichIcon type={RichIconTypes.HAAND_HEIHEI} />}
        />
    );
};

type VirtualCardContentProps = {
    digitalCard: VirtualCardI;
    enabled: boolean;
    isError: boolean;
    error: Error | null;
    tryAgain(): void;
};

const VirtualCardContent = ({ enabled, isError, error, tryAgain, digitalCard }: VirtualCardContentProps) => {
    if (!enabled) {
        return <VirtualCardNotAvailable />;
    }

    if (isError) {
        return <CouldNotFetchVirtualCard error={error} tryAgainClick={tryAgain} />;
    }

    return <VirtualCard digitalCard={digitalCard} />;
};

const DIGITAL_CARD_COUNTDOWN_MS = 60000;

export const VirtualCardModal = ({ card, open, onClose, setOpen }: VirtualCardModalProps) => {
    const text = useText();
    const isHighFraudRisk = useIsHighFraudRisk();

    const enabled = !isHighFraudRisk && open;

    const { digitalCardTitle, loadingDigitalCard } = text;
    const {
        data: virtualCard,
        isError,
        error,
        isLoading,
        isSuccess,
        refetch: fetchDigitalCard,
    } = useKbVirtualCreditCard(card, { enabled: open });

    const query = useQueryClient();

    return (
        <StateHandlingModal
            appNamespace={DEFAULT_APP_NAMESPACE}
            setOpen={setOpen}
            onClose={() => {
                setOpen(false);
                onClose?.();
                query.removeQueries({ queryKey: ['virtualCard', card] });
            }}
            id="digital-card"
            open={open}
            countdown={isSuccess ? DIGITAL_CARD_COUNTDOWN_MS : undefined}
            title={digitalCardTitle}
            shouldCloseOnOverlayClick
            centerContentHorizontally
            loading={isLoading}
            loadingText={loadingDigitalCard}
        >
            <VirtualCardContent
                digitalCard={virtualCard!}
                isError={isError || virtualCard === undefined || !virtualCard.imageUrl}
                enabled={enabled}
                tryAgain={fetchDigitalCard}
                error={error}
            />
        </StateHandlingModal>
    );
};
